import { multiSelectOptions, selectOption } from '@/components/ui/multi-select-utils.js';
import { formatPlainDate, intString } from '@airreading/common';
import {
  AttendanceStatus,
  AttendanceStatusLabelMap,
  attendanceStatusSchema,
  AttendanceStatusSelectable,
  attendanceStatusSelectableSchema,
  byStudentYear,
  CoachAttendanceLateTagRegex,
  CoachAttendanceStatus,
  CoachAttendanceStatusLabelMap,
  coachAttendanceStatusSchema,
  CoachAttendanceTagLabelMap,
  GetCoachAttendanceRollupResult,
  studentYearOption,
  StudentYearSelectable,
  STUDENT_GROUP_NOTES_MAX_LENGTH,
} from '@airreading/common/data';
import { Temporal } from 'temporal-polyfill';
import { z } from 'zod';
import { createFormOptions } from './form-utils.js';

export const formCreateEditClassSchema = z.object({
  coach: selectOption.optional(),
  students: multiSelectOptions.refine((arr) => arr.length > 0, { message: 'At least one student is required' }),
  schedule: selectOption.refine(({ value }) => !!value, { message: 'Required' }),
  overrideStartPlainDate: z.instanceof(Temporal.PlainDate).optional(),
  overrideEndPlainDate: z.instanceof(Temporal.PlainDate).optional(),
  classTime: selectOption.refine(({ value }) => !!value, { message: 'Required' }),
  learningPath: selectOption.refine(({ value }) => !!value, { message: 'Required' }),
  startingLesson: selectOption.refine(({ value }) => !!value, { message: 'Required' }),
  teacherType: z.string().optional(),
  coachAvailable: selectOption.optional(),
  coachAll: selectOption.optional(),
  attendanceStatus: attendanceStatusSelectableSchema,
  emailsSent: multiSelectOptions,
  coachUnassignedAtPlainDate: z.instanceof(Temporal.PlainDate).optional(),
  attendanceNotes: z.string().nullable().optional(),
  notes: z
    .string()
    .max(STUDENT_GROUP_NOTES_MAX_LENGTH, `Notes must be ${STUDENT_GROUP_NOTES_MAX_LENGTH} characters or fewer`)
    .nullable()
    .optional(),
  protectedPeriodOverrideReason: z.string().optional(),
});

export const formCreateEditOneTimeClassSchema = z.object({
  coach: selectOption.optional(),
  students: multiSelectOptions.refine((arr) => arr.length > 0, { message: 'At least one student is required' }),
  schedule: selectOption.refine(({ value }) => !!value, { message: 'Required' }),
  overrideTime: z.boolean().default(false),
  overrideStartPlainDate: z.instanceof(Temporal.PlainDate).optional(),
  overrideStartPlainTime: z.instanceof(Temporal.PlainTime).optional(),
  overrideStartDurationInMin: intString.default('30'),
  classTime: selectOption.refine(({ value }) => !!value, { message: 'Required' }),
  learningPath: selectOption.refine(({ value }) => !!value, { message: 'Required' }),
  startingLesson: selectOption.refine(({ value }) => !!value, { message: 'Required' }),
  teacherType: z.string().optional(),
  coachAvailable: selectOption.optional(),
  coachAll: selectOption.optional(),
  notes: z
    .string()
    .max(STUDENT_GROUP_NOTES_MAX_LENGTH, `Notes must be ${STUDENT_GROUP_NOTES_MAX_LENGTH} characters or fewer`)
    .nullable()
    .optional(),
});

export const formUpdateBulkActionsSchema = z.object({
  updateType: z.string(),
  startPlainDate: z.instanceof(Temporal.PlainDate),
  endPlainDate: z.instanceof(Temporal.PlainDate),
  schedule: selectOption.optional(),
  classTime: selectOption.optional(),
  coach: selectOption.optional(),
  learningPath: selectOption.optional(),
  startingLesson: selectOption.optional(),
  students: multiSelectOptions.optional(),
  teacherType: z.string().optional(),
  coachAvailable: selectOption.optional(),
  coachAll: selectOption.optional(),
  reassigned: z.boolean().optional(),
  sendConfirmationEmail: z.boolean().default(false),
  protectedPeriodOverrideReason: z.string().optional(),
});

export const formatStudentGrades = (grades: string[] | null | undefined): string => {
  if (!grades || grades.length === 0) {
    return 'N/A';
  }

  const sortedGrades = grades.sort(byStudentYear as (a: string, b: string) => number);

  return sortedGrades.map((grade) => studentYearOption(grade as StudentYearSelectable)).join(', ');
};

export function classifyCoachAttendanceTag(tag: string): string | null {
  const normalizedTag = tag.trim();

  if (normalizedTag === CoachAttendanceTagLabelMap.Sub) {
    return CoachAttendanceTagLabelMap.Sub;
  }
  if (normalizedTag === CoachAttendanceTagLabelMap.SubEmergency) {
    return CoachAttendanceTagLabelMap.SubEmergency;
  }
  if (normalizedTag === CoachAttendanceTagLabelMap.SubExcused) {
    return CoachAttendanceTagLabelMap.SubExcused;
  }
  if (normalizedTag === CoachAttendanceTagLabelMap.SubEmergencyExcused) {
    return CoachAttendanceTagLabelMap.SubEmergencyExcused;
  }
  if (normalizedTag === CoachAttendanceTagLabelMap.TeacherNoShow) {
    return CoachAttendanceTagLabelMap.TeacherNoShow;
  }
  if (normalizedTag === CoachAttendanceTagLabelMap.Late || normalizedTag.startsWith('(Late - ')) {
    return CoachAttendanceTagLabelMap.Late;
  }

  return null;
}

export type CoachAttendanceDisplayDate = { text: string; reason?: string };

// Utility to group tags by categories and format output
export const formatCoachAttendanceRollup = (
  coachAttendanceRollup: GetCoachAttendanceRollupResult,
  limits: { maxPlannedAbsences: number; shortNoticeSubRequestLimit: number },
) => {
  if (
    !coachAttendanceRollup ||
    coachAttendanceRollup.status === CoachAttendanceStatus.Perfect ||
    coachAttendanceRollup.items.length === 0
  ) {
    return null;
  }

  const categoryMap: {
    [display: string]: {
      dates: CoachAttendanceDisplayDate[];
      detail?: (total: number) => string;
    };
  } = {
    [CoachAttendanceTagLabelMap.TeacherNoShow]: {
      dates: [],
      detail: (total: number) => ` (${total})`,
    },
    [CoachAttendanceTagLabelMap.SubEmergency]: {
      dates: [],
      detail: (total: number) => ` (${total} of ${limits.shortNoticeSubRequestLimit})`,
    },
    [CoachAttendanceTagLabelMap.Sub]: {
      dates: [],
      detail: (total: number) => ` (${total} of ${limits.maxPlannedAbsences})`,
    },

    [CoachAttendanceTagLabelMap.Late]: {
      dates: [],
      detail: (total: number) => ` (${total})`,
    },
    [CoachAttendanceTagLabelMap.SubEmergencyExcused]: {
      dates: [],
      detail: (total: number) => ` (${total})`,
    },
    [CoachAttendanceTagLabelMap.SubExcused]: {
      dates: [],
      detail: (total: number) => ` (${total})`,
    },
  };

  // Helper to format date as M/D
  function formatDateToMD(date: Temporal.PlainDate | string): string {
    const plainDate = typeof date === 'string' ? Temporal.PlainDate.from(date) : date;
    return formatPlainDate(plainDate, { includeYear: false });
  }

  // Build up categoryMap with formatted dates
  for (const item of coachAttendanceRollup.items) {
    for (const tag of item.tags) {
      const category = classifyCoachAttendanceTag(tag);
      if (!category) {
        continue;
      }
      // Late with minutes pattern: (Late - 23 mins)
      if (category === CoachAttendanceTagLabelMap.Late) {
        const minutesMatch = tag.match(CoachAttendanceLateTagRegex);
        if (tag.startsWith('(Late - ') && minutesMatch) {
          categoryMap[CoachAttendanceTagLabelMap.Late].dates.push({
            text: `${formatDateToMD(item.plainDate)} (${minutesMatch[1]} mins)`,
          });
        } else {
          categoryMap[CoachAttendanceTagLabelMap.Late].dates.push({ text: formatDateToMD(item.plainDate) });
        }
      } else {
        categoryMap[category].dates.push({
          text: formatDateToMD(item.plainDate),
          reason: item.reasonsByTag?.[tag],
        });
      }
    }
  }

  return categoryMap;
};

export function formatCoachAttendanceRollupLine({ status, items }: GetCoachAttendanceRollupResult): string {
  if (status === CoachAttendanceStatus.Perfect || items.length === 0) {
    return CoachAttendanceStatusLabelMap.Perfect;
  }

  return items
    .map((item) => {
      const classDate =
        item.plainDate instanceof Temporal.PlainDate ? item.plainDate : Temporal.PlainDate.from(item.plainDate);
      return `${formatPlainDate(classDate)} ${item.tags.join(', ')}`;
    })
    .join(', ');
}

export const coachAttendanceStatusOptions = createFormOptions<CoachAttendanceStatus>(
  coachAttendanceStatusSchema.options,
  CoachAttendanceStatusLabelMap,
);

export const attendanceStatusOptions = createFormOptions<AttendanceStatus>(
  attendanceStatusSchema.options,
  AttendanceStatusLabelMap,
);

export function getAttendanceStatusLabel(attendanceStatus: AttendanceStatusSelectable | null | undefined): string {
  if (!attendanceStatus || attendanceStatus === 'null') return '(empty)';
  return AttendanceStatusLabelMap[attendanceStatus];
}
